import type { OnboardingSession } from "@clientportal/core/types";

import { api } from "@/shared/api/api";

const onboardingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createOnboardingSession: builder.mutation<{ result: string; sessionId: string }, { cognitoId: string }>({
      query: ({ cognitoId }) => ({
        url: `/onboarding/session/${cognitoId}`,
        method: "POST",
      }),
      invalidatesTags: ["Policy", "OnboardingSession"],
    }),
    getOnboardingSession: builder.query<OnboardingSession, { cognitoId: string; sessionId: string }>({
      query: ({ cognitoId, sessionId }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}`,
        method: "GET",
      }),
      providesTags: ["OnboardingSession"],
    }),
    updateOnboardingSession: builder.mutation<void, { cognitoId: string; sessionId: string; body: object }>({
      query: ({ cognitoId, sessionId, body }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}`,
        method: "PUT",
        body: { data: body },
      }),
      onQueryStarted(_req, { dispatch, queryFulfilled }) {
        queryFulfilled.catch(() => dispatch(api.util.invalidateTags(["OnboardingSession"])));
      },
    }),
    processOnboardingSession: builder.mutation<Array<string>, { cognitoId: string; sessionId: string }>({
      query: ({ cognitoId, sessionId }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}/process`,
        method: "POST",
      }),
      invalidatesTags: ["OnboardingSession"],
    }),
    discardOnboardingSessionDocs: builder.mutation<void, { cognitoId: string; sessionId: string }>({
      query: ({ cognitoId, sessionId }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}/discard_docs`,
        method: "POST",
      }),
      invalidatesTags: ["OnboardingSession"],
    }),
    onboardingDocsComplete: builder.mutation<void, { cognitoId: string; sessionId: string }>({
      query: ({ cognitoId, sessionId }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}/complete`,
        method: "PUT",
      }),
      invalidatesTags: ["OnboardingSession"],
    }),
    getOnboardingFileMetadata: builder.query<
      { filename: string; uploadedAt: number },
      { cognitoId: string; sessionId: string; fieldKey: string }
    >({
      query: ({ cognitoId, sessionId, fieldKey }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}/file/${fieldKey}`,
        method: "GET",
      }),
      providesTags: ["OnboardingSessionFiles"],
    }),
    getPresignedUploadUrl: builder.mutation<
      { presignedUrl: string; documentMetadataId: string; s3Key: string },
      { cognitoId: string; sessionId: string; fieldKey: string; filename: string; contentType: string }
    >({
      query: ({ cognitoId, sessionId, fieldKey, filename, contentType }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}/upload`,
        method: "POST",
        body: { fieldKey, filename, contentType },
      }),
    }),
    completeOnboardingUpload: builder.mutation<
      { documentMetadataId: string; session: OnboardingSession },
      { cognitoId: string; sessionId: string; fieldKey: string; documentMetadataId: string; filename: string }
    >({
      query: ({ cognitoId, sessionId, fieldKey, documentMetadataId }) => ({
        url: `/onboarding/session/${cognitoId}/${sessionId}/upload-complete`,
        method: "PUT",
        body: { fieldKey, documentMetadataId },
      }),
      async onQueryStarted(
        { cognitoId, sessionId, fieldKey, documentMetadataId, filename },
        { dispatch, queryFulfilled },
      ) {
        // RTK Query limitation: base api has no endpoints, so updateQueryData types
        // the endpoint name as never. Cast to any is the standard workaround.
        dispatch(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (api.util.updateQueryData as any)(
            "getOnboardingSession",
            { cognitoId, sessionId },
            (draft: OnboardingSession | undefined) => {
              if (draft) {
                if (documentMetadataId) {
                  draft.data = { ...draft.data, [fieldKey]: documentMetadataId };
                } else {
                  const newData = { ...draft.data };
                  delete (newData as Record<string, unknown>)[fieldKey];
                  draft.data = newData;
                }
                draft.updatedAt = new Date().toISOString();
              }
            },
          ),
        );

        if (documentMetadataId) {
          dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (api.util.upsertQueryData as any)(
              "getOnboardingFileMetadata",
              { cognitoId, sessionId, fieldKey },
              { filename, uploadedAt: Date.now() },
            ),
          );
        } else {
          dispatch(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (api.util.updateQueryData as any)(
              "getOnboardingFileMetadata",
              { cognitoId, sessionId, fieldKey },
              () => undefined,
            ),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          dispatch(api.util.invalidateTags(["OnboardingSession", "OnboardingSessionFiles"]));
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateOnboardingSessionMutation,
  useGetOnboardingSessionQuery,
  useUpdateOnboardingSessionMutation,
  useProcessOnboardingSessionMutation,
  useDiscardOnboardingSessionDocsMutation,
  useOnboardingDocsCompleteMutation,
  useGetOnboardingFileMetadataQuery,
  useGetPresignedUploadUrlMutation,
  useCompleteOnboardingUploadMutation,
  endpoints,
} = onboardingApi;

export { onboardingApi };
